import { useContext, useState } from "react";
import { Copy } from "lucide-react";
import { Button, PlatformIcon, Spinner, Icon } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useCapability } from "../../../app/bandContext.js";
import { useRelease, useSmartLinkStats, useSyncDspLinks } from "../../../app/hooks/useRelease.js";
import { pushToast } from "../../../app/toasts.js";
import { PUBLIC_WEB_URL } from "../../../config/env.js";
import { SectionRail } from "../sectionRail.js";
import { ReleaseSectionNavContext } from "../sectionRailContext.js";
import { dspSyncMessage } from "../dspSyncMessage.js";
import { MediaImage } from "../MediaImage.js";
import { SmartLinkEditorModal } from "../SmartLinkEditorModal.js";
import { DspResolveModal } from "../DspResolveModal.js";
import { SmartLinkStatTiles, DspClicksChart } from "../SmartLinkStats.js";
import { DSPS } from "../dsp.js";
import styles from "../section.module.scss";
import own from "./SmartLinkSection.module.scss";

type DspLinkDto = components["schemas"]["DspLinkDto"];

// Smart Link (shot-12) — the phone-mockup landing preview + the copyable public URL + the per-DSP URL
// editor (EDIT SMART LINK) + the visit/click stat tiles & lifetime DSP-clicks bar chart. The slug + DSP URLs are
// real (PUT smart-link / dsp-links, If-Match). The tiles + chart read GET /smart-link/stats from the
// analytics rollup projection; they honour the leak-test contract — a null metric renders "—", never a fabricated
// number (the visit→click ratio is null until there are visits). The public landing PAGE itself is .

export function SmartLinkSection({ releaseId }: { releaseId: string }) {
  const canWrite = useCapability("releases:write");
  const q = useRelease(releaseId);
  const statsQuery = useSmartLinkStats(releaseId);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [resolving, setResolving] = useState(false);
  const goToSection = useContext(ReleaseSectionNavContext);
  const syncLinks = useSyncDspLinks(releaseId);

  const cc = q.data?.data;
  const version = q.data?.version;
  const smartLink = cc?.smartLink;

  if (q.isLoading || !smartLink) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading smart link…" />
      </div>
    );
  }

  const overview = cc?.overview;
  const dspLinks = cc?.dspLinks ?? [];
  // ART-219: DSPs the sync couldn't auto-pick (the recording is listed twice) — a manager taps the right one.
  const dspCandidates = cc?.dspCandidates ?? [];
  // ART-219: the buttons are looked up from the release's songs — the lead song's Spotify id — so a release with
  // no song from Spotify can never get any. Say THAT, rather than leave an empty page that reads as "still
  // looking", and point at the one place that fixes it.
  const noSpotifySongs = !(cc?.tracks ?? []).some((t) => Boolean(t.spotifyTrackId));
  const noLinksBecauseNoSongs = dspLinks.length === 0 && noSpotifySongs;
  // …and the other case: there IS a Spotify song, the lookup just has not filled anything yet (it runs on its own
  // clock). Offer to run it now. Not while a pick is pending — that is "Resolve links" in the rail, not a fetch.
  const linksNotSyncedYet = dspLinks.length === 0 && !noSpotifySongs && dspCandidates.length === 0;

  async function runLinkSync() {
    try {
      const r = await syncLinks.mutateAsync();
      const [message, tone] = dspSyncMessage(r);
      pushToast(message, tone);
    } catch {
      // The hook toasted (the once-a-minute 429 included).
    }
  }
  const publicUrl = absolutePublicUrl(smartLink.publicPath);
  const artist = prettifySlug(smartLink.bandSlug);

  async function copy() {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className={styles.section}>
      <SectionRail>
        {canWrite && dspCandidates.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setResolving(true)}>
            Resolve links ({dspCandidates.length})
          </Button>
        ) : null}
        {canWrite ? (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            Edit smart link
          </Button>
        ) : null}
      </SectionRail>

      <div className={own.layout}>
        <PhonePreview
          coverMediaId={overview?.coverMediaId}
          coverImageUrl={overview?.coverImageUrl}
          title={overview?.title}
          artist={artist}
          seed={releaseId}
          dspLinks={dspLinks}
        />

        <div className={own.right}>
          {smartLink.slug ? (
            <div className={own.urlRow}>
              <span className={own.url}>{publicUrl ?? smartLink.publicPath}</span>
              <Button variant="ghost" size="sm" onClick={() => void copy()} disabled={!publicUrl}>
                <Icon icon={Copy} size={16} /> {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          ) : (
            <p className={own.note}>
              No public slug yet.{canWrite ? " Choose one with Edit smart link to publish this release's page." : ""}
            </p>
          )}

          <SmartLinkStatTiles stats={statsQuery.data} isError={statsQuery.isError} />

          <DspClicksChart
            dspLinks={dspLinks}
            stats={statsQuery.data}
            isError={statsQuery.isError}
            emptyAction={
              linksNotSyncedYet && canWrite ? (
                <div className={own.callout} role="status">
                  <p className={own.calloutText}>Sync DSP links to start tracking clicks.</p>
                  <Button variant="ghost" size="sm" onClick={() => void runLinkSync()} disabled={syncLinks.isPending} aria-busy={syncLinks.isPending || undefined}>
                    Sync DSP Links
                  </Button>
                </div>
              ) : noLinksBecauseNoSongs ? (
                <div className={own.callout} role="status">
                  <p className={own.calloutText}>Unable to fetch links with no established tracks.</p>
                  {canWrite && goToSection && (
                    <Button variant="ghost" size="sm" onClick={() => goToSection("assets")}>
                      Go to Assets
                    </Button>
                  )}
                </div>
              ) : undefined
            }
          />
        </div>
      </div>

      {editing && version !== undefined && (
        <SmartLinkEditorModal
          releaseId={releaseId}
          version={version}
          currentSlug={smartLink.slug ?? ""}
          bandSlug={smartLink.bandSlug ?? ""}
          currentLinks={dspLinks}
          onClose={() => setEditing(false)}
        />
      )}

      {resolving && version !== undefined && (
        <DspResolveModal
          releaseId={releaseId}
          version={version}
          currentLinks={dspLinks}
          candidates={dspCandidates}
          onClose={() => setResolving(false)}
        />
      )}
    </div>
  );
}

// ---- phone-mockup landing preview ----------------------------------------------------------------------

function PhonePreview({
  coverMediaId,
  coverImageUrl,
  title,
  artist,
  seed,
  dspLinks,
}: {
  coverMediaId?: string | null;
  coverImageUrl?: string | null;
  title?: string | null;
  artist: string;
  seed: string;
  dspLinks: DspLinkDto[];
}) {
  const shown = DSPS.filter((d) => dspLinks.some((l) => l.dsp === d.key && l.url));
  return (
    <div className={own.phone} role="group" aria-label="Landing page preview">
      <div className={own.phoneNotch} aria-hidden="true" />
      <div className={own.phoneScreen}>
        <MediaImage mediaId={coverMediaId} url={coverImageUrl} seed={seed} alt="" className={own.phoneCover} lazy={false} />
        <p className={own.phoneTitle}>{title || "Untitled release"}</p>
        {artist && <p className={own.phoneArtist}>{artist}</p>}
        <div className={own.phoneButtons}>
          {shown.length === 0 ? (
            <p className={own.phoneEmpty}>Add streaming links to preview the DSP buttons.</p>
          ) : (
            shown.map((d) => (
              <div key={d.key} className={own.phoneBtn}>
                <span className={own.phoneBtnIcon} aria-hidden="true">
                  <PlatformIcon platform={d.key} size={20} />
                </span>
                <span className={own.phoneBtnName}>{d.label}</span>
                <span className={own.phoneBtnPlay}>Play</span>
              </div>
            ))
          )}
        </div>
        <p className={own.phonePowered}>Powered by Artist CNTRL</p>
      </div>
    </div>
  );
}

// ---- helpers -------------------------------------------------------------------------------------------

function absolutePublicUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  const base = PUBLIC_WEB_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${path}`;
}

/** Turn a band slug ("die-like-one") into a display artist name ("Die Like One") for the preview. */
function prettifySlug(slug: string | null | undefined): string {
  if (!slug) return "";
  return slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
